import { gql } from '@apollo/client';
import _ from 'lodash';
import { FormGroup, Label } from 'reactstrap';
import { useFormikContext } from 'formik';
import { UsersQuery } from '../../queries';
import { useIsSuperadminRoute, useQueryWithMsg } from '../../utils/hooks';
import FormTypeahead from '../FormTypeahead';

const FarmUsersQuery = gql`
  query FarmUsersQuery($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      members {
        id
        email
        isPending
        role
      }
      team {
        id
      }
    }
  }
`;

const FarmAndTeamUsersQuery = gql`
  query FarmAndTeamUsersQuery($farmId: ID!, $teamId: ID!) {
    farm(farmId: $farmId) {
      id
      members {
        id
        email
        isPending
        role
      }
    }
    team(id: $teamId) {
      id
      members {
        id
        email
        isPending
        role
      }
    }
  }
`;

const canEdit = ({ role }) => role === 'editor' || role === 'admin';

// Get all users that are editor or admin on the farm.
const getPossibleOwners = (data) => {
  const farmMembers = data?.farm?.members || [];
  const teamMembers = data?.team?.members || [];
  const owners = _.uniqBy(
    [...farmMembers, ...teamMembers].filter(canEdit),
    'id',
  );
  return _.sortBy(owners, 'email');
};

const OwnerTypeahead = ({ showLabels, options = [], msg, ...props }) => {
  const { values } = useFormikContext();
  return (
    <FormGroup>
      {showLabels && <Label>Owner</Label>}
      <FormTypeahead
        clearTextOnFocus
        id="owner"
        name="owner"
        labelKey="email"
        options={options}
        placeholder={msg || 'Select a user...'}
        selectedOption={values.owner}
        size="sm"
        {...props}
      />
    </FormGroup>
  );
};

const SuperadminSelector = (props) => {
  const { msg, data } = useQueryWithMsg(UsersQuery);
  return <OwnerTypeahead {...props} options={data?.users} msg={msg} />;
};

const FarmOwnerSelector = ({ farmId, ...props }) => {
  const { values } = useFormikContext();
  const teamId = values.team?.id;
  const { msg, data } = useQueryWithMsg(
    teamId ? FarmAndTeamUsersQuery : FarmUsersQuery,
    {
      variables: { farmId, teamId },
    },
  );
  const options = getPossibleOwners(data);
  if (options.length === 0) return null;
  return <OwnerTypeahead {...props} options={options} msg={msg} />;
};

const OwnerSelector = (props) => {
  const isSuperadminRoute = useIsSuperadminRoute();
  return isSuperadminRoute ? (
    <SuperadminSelector {...props} />
  ) : (
    <FarmOwnerSelector {...props} />
  );
};

export default OwnerSelector;
